import React, { useState, useCallback } from "react";
import { Button } from "antd";
import { CopyOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { usePeerConnection } from "features/robot-teleop/hooks/usePeerConnection";
import { usePeerStats } from "features/robot-teleop/hooks/usePeerStats";
import { fmtMs, fmtNum, fmtKbps, fmtPct, fmtPath, gradeText, GRADE_LETTER } from "./linkPresentation";

// strip the raw getStats objects kept for delta computation
const stripRaw = (s) => (s ? { ...s, raw: undefined } : s);

const Row = ({ label, value, bad }) => (
  <tr>
    <td>{label}</td>
    <td className={`v ${bad ? "bad" : ""}`}>{value}</td>
  </tr>
);

/**
 * Full live metric table of one peer (rendered inside the badge Popover; it
 * subscribes on its own so the numbers keep updating while open).
 */
const PeerStatsDetails = ({ peerId }) => {
  const { t } = useTranslation();
  const { state, connectionState, iceState, retryCount, cameras, microphones, speakers, isControlChannel } = usePeerConnection(peerId);
  const { sample, history, health, grade } = usePeerStats(peerId, { historySize: 60 });
  const [copied, setCopied] = useState(false);
  const v = sample?.video;
  const a = sample?.audio;
  const lastPacketAge = v?.lastPacketReceivedTimestamp != null && sample?.timestamp != null
    ? Math.max(0, (sample.timestamp - v.lastPacketReceivedTimestamp) / 1000)
    : null;

  const copyDiagnostics = useCallback(() => {
    const payload = {
      peerId, state, connectionState, iceState, retryCount, health, grade,
      devices: { cameras, microphones, speakers, isControlChannel },
      sample: stripRaw(sample),
      history: history.map(stripRaw),
      copiedAt: new Date().toISOString(),
    };
    const text = JSON.stringify(payload, null, 2);
    const done = () => { setCopied(true); setTimeout(() => setCopied(false), 1500); };
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text).then(done).catch(() => {});
    }
  }, [peerId, state, connectionState, iceState, retryCount, health, grade, cameras, microphones, speakers, isControlChannel, sample, history]);

  const ms = t("teleop.peer.metric.ms");
  const d = (key) => t(`teleop.peer.details.${key}`);

  return (
    <div className="peer-stats-details">
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 4 }}>
        <Button size="small" icon={<CopyOutlined />} onClick={copyDiagnostics}>
          {copied ? d("copied") : d("copy")}
        </Button>
      </div>
      <table>
        <tbody>
          <tr className="section"><td colSpan={2} className="section">{d("link")}</td></tr>
          <Row label={d("peer")} value={`${peerId}${isControlChannel ? ` ${t("teleop.peer.badge.controlSuffix")}` : ""}`} />
          <Row label={d("state")} value={`${state}${connectionState ? ` · ${connectionState}` : ""}${iceState ? ` · ice ${iceState}` : ""}`} bad={connectionState && connectionState !== "connected"} />
          <Row label={d("retries")} value={fmtNum(retryCount)} />
          <Row label={d("grade")} value={grade ? `${GRADE_LETTER[grade]} (${gradeText(grade, t)})` : "–"} bad={grade === "critical"} />
          <Row label={d("delay")} value={`${fmtMs(sample?.rttMs)} ${ms}`} bad={sample?.rttMs > 300} />
          <Row label={d("path")} value={fmtPath(sample?.path, t)} />
          <Row label={d("devices")} value={t("teleop.peer.details.devicesValue", { cameras: cameras.length, mics: microphones.length, speakers: speakers.length })} />
          <tr className="section"><td colSpan={2} className="section">{d("video")}</td></tr>
          <Row label={d("health")} value={health} bad={health === "stalled" || health === "no-track"} />
          <Row label={d("jitter")} value={`${fmtMs(v?.jitterMs, 1)} ${ms}`} bad={v?.jitterMs > 60} />
          <Row label={d("jitterBuffer")} value={`${fmtMs(v?.jitterBufferDelayMs)} ${ms}`} />
          <Row label={d("frameRate")} value={`${fmtNum(v?.fps, 1)} ${t("teleop.peer.metric.fpsUnit")}`} bad={v && v.fps != null && v.fps < 5} />
          <Row label={d("resolution")} value={v?.frameWidth ? `${v.frameWidth}×${v.frameHeight}` : "–"} />
          <Row label={d("bitrate")} value={fmtKbps(v?.bitrateKbps)} />
          <Row label={d("packetLoss")} value={fmtPct(v?.packetLossPct)} bad={v?.packetLossPct > 3} />
          <Row label={d("framesDecoded")} value={fmtNum(v?.framesDecoded)} />
          <Row label={d("freezes")} value={v?.freezeCount != null ? `${v.freezeCount} (${fmtMs(v.totalFreezesDurationMs)} ${ms})` : "–"} />
          <Row label={d("lastPacket")} value={lastPacketAge != null ? t("teleop.peer.details.lastPacketAgo", { s: lastPacketAge.toFixed(1) }) : "–"} bad={lastPacketAge > 2} />
          <Row label={d("codec")} value={v?.codec ? `${v.codec}${v.decoderImplementation ? ` · ${v.decoderImplementation}` : ""}` : "–"} />
          <tr className="section"><td colSpan={2} className="section">{d("audio")}</td></tr>
          <Row label={d("jitter")} value={`${fmtMs(a?.jitterMs, 1)} ${ms}`} />
          <Row label={d("bitrate")} value={fmtKbps(a?.bitrateKbps)} />
          <Row label={d("packetLoss")} value={fmtPct(a?.packetLossPct)} />
          <Row label={d("level")} value={a?.audioLevel != null ? a.audioLevel.toFixed(2) : "–"} />
        </tbody>
      </table>
    </div>
  );
};

export default PeerStatsDetails;
